import React, { useState, useEffect } from 'react';
import './ShareModal.css';

const ShareModal = ({ isOpen, onClose, shareText = '' }) => {
  const [copied, setCopied] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Generate share URL with text parameter
      const baseUrl = window.location.origin;
      const encodedText = encodeURIComponent(shareText);
      const url = shareText ? `${baseUrl}/?text=${encodedText}` : baseUrl;
      setShareUrl(url);
      setCopied(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, shareText]);

  // Handle Facebook share
  const handleFacebookShare = () => {
    const url = encodeURIComponent(shareUrl);
    const text = encodeURIComponent(shareText || 'Check out this LED Scroller!');
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${url}&quote=${text}`;
    window.open(facebookUrl, '_blank', 'width=600,height=400');
    
    // Track Facebook share
    if (window.gtag && window.cookieConsentGiven) {
      window.gtag('event', 'share_facebook', {
        event_category: 'Social Share',
        event_label: 'Facebook Share',
        value: 1
      });
    }
  };

  // Handle Instagram share (Instagram doesn't support direct web sharing, so we'll open Instagram web)
  const handleInstagramShare = () => {
    // Instagram doesn't support direct web sharing, so we'll copy the link and show a message
    // Or we can open Instagram web with a message
    alert('Instagram doesn\'t support direct web sharing. Please copy the link and paste it in your Instagram story or post.');
    
    // Track Instagram share attempt
    if (window.gtag && window.cookieConsentGiven) {
      window.gtag('event', 'share_instagram', {
        event_category: 'Social Share',
        event_label: 'Instagram Share Attempt',
        value: 1
      });
    }
  };

  // Handle WhatsApp share
  const handleWhatsAppShare = () => {
    const text = encodeURIComponent(`${shareText || 'Check out this LED Scroller!'} - ${shareUrl}`);
    const whatsappUrl = `https://wa.me/?text=${text}`;
    window.open(whatsappUrl, '_blank');
    
    // Track WhatsApp share
    if (window.gtag && window.cookieConsentGiven) {
      window.gtag('event', 'share_whatsapp', {
        event_category: 'Social Share',
        event_label: 'WhatsApp Share',
        value: 1
      });
    }
  };

  // Handle copy link
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      
      // Track copy link
      if (window.gtag && window.cookieConsentGiven) {
        window.gtag('event', 'share_copy_link', {
          event_category: 'Social Share',
          event_label: 'Copy Link',
          value: 1
        });
      }

      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
      // Fallback: select text
      const input = document.createElement('input');
      input.value = shareUrl;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div className="share-modal-container" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button className="share-modal-close" onClick={onClose} aria-label="Close">
          ×
        </button>

        {/* Title */}
        <h2 className="share-modal-title">Share LED Display</h2>

        {/* Social Media Buttons */}
        <div className="share-modal-social-buttons">
          <button 
            className="share-social-btn share-facebook" 
            onClick={handleFacebookShare}
            aria-label="Share on Facebook"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
            </svg>
            <span>Facebook</span>
          </button>

          <button 
            className="share-social-btn share-instagram" 
            onClick={handleInstagramShare}
            aria-label="Share on Instagram"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            <span>Instagram</span>
          </button>

          <button 
            className="share-social-btn share-whatsapp" 
            onClick={handleWhatsAppShare}
            aria-label="Share on WhatsApp"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            <span>WhatsApp</span>
          </button>
        </div>

        {/* Copy Link Section */}
        <div className="share-modal-copy-section">
          <p className="share-modal-copy-label">Or copy this link</p>
          <div className="share-modal-input-group">
            <input
              type="text"
              className="share-modal-input"
              value={shareUrl}
              readOnly
              aria-label="Share URL"
            />
            <button
              className={`share-modal-copy-btn ${copied ? 'copied' : ''}`}
              onClick={handleCopyLink}
              aria-label="Copy link"
            >
              {copied ? (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  Copied
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/>
                  </svg>
                  Copy
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;

